import { LoadConfirmFormContainer } from '../forms.styles'
import TextInputField from '../../fields/text-input-field/text-input-field.component'
import FormTwoColumnLayout from '../../../layouts/form-two-column-layout/form-two-column-layout'
import FormSingleColumnLayout from '../../../layouts/form-single-column-layout/form-single-column-layout'
import { useFormContext } from 'react-hook-form'
import { useEffect, useRef } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import DatePickerField from 'components/fields/date-range-picker-field/date-range-picker-field.component'
import TimeInputField from 'components/fields/time-input-field/time-input-field.component'
import OrderEventCompanyNameField from 'components/fields/order-event-company-name-field/order-event-company-name-field.component'
import { useGetOrderEventCompanySuggestionsQuery } from 'store/orders/orders.api'
import { getUserId } from 'utils/common'
import { combineDateAndTime, extractDate, extractTime } from 'utils/date-time'

interface LoadPickupFormProps {
  eventIndex?: number
  hideIcons?: boolean
  /** Fetch company suggestions. Only enable for order creation flows. */
  enableCompanySuggestions?: boolean
}

const LoadPickupForm = ({
  eventIndex = 0,
  hideIcons = false,
  enableCompanySuggestions = false,
}: LoadPickupFormProps) => {
  const { watch, setValue, getValues } = useFormContext()
  const userId = getUserId()
  const { data: companySuggestions = [], isFetching: companySuggestionsLoading } =
    useGetOrderEventCompanySuggestionsQuery(
      enableCompanySuggestions && userId ? { eventType: 'PICKUP' } : skipToken
    )
  const isInitializing = useRef(true)
  const skipDateUpdate = useRef(false)
  const skipTimeUpdate = useRef(false)

  // Watch the combined datetime fields
  const startTime = watch(`orderEvents.${eventIndex}.startTime`)
  const endTime = watch(`orderEvents.${eventIndex}.endTime`)

  // Use internal field names for time only (date uses the actual startTime field)
  const startTimeOnlyField = `_internal_startTimeOnly_${eventIndex}`
  const endTimeOnlyField = `_internal_endTimeOnly_${eventIndex}`

  const startTimeOnly = watch(startTimeOnlyField)
  const endTimeOnly = watch(endTimeOnlyField)

  const lastSyncedStartTime = useRef<string | null>(null)
  const lastSyncedEndTime = useRef<string | null>(null)

  // Reset sync tracking when switching between order events
  useEffect(() => {
    lastSyncedStartTime.current = null
    lastSyncedEndTime.current = null
    isInitializing.current = true
  }, [eventIndex])

  // Keep internal time fields in sync when startTime/endTime change (e.g. after form reset)
  useEffect(() => {
    if (startTime !== lastSyncedStartTime.current) {
      lastSyncedStartTime.current = startTime ?? null
      setValue(
        startTimeOnlyField,
        startTime ? extractTime(startTime) : '',
        { shouldValidate: false }
      )
    }
    if (endTime !== lastSyncedEndTime.current) {
      lastSyncedEndTime.current = endTime ?? null
      setValue(
        endTimeOnlyField,
        endTime ? extractTime(endTime) : '',
        { shouldValidate: false }
      )
    }
    if (isInitializing.current) {
      isInitializing.current = false
    }
  }, [startTime, endTime, setValue, startTimeOnlyField, endTimeOnlyField])

  // When startTime changes (from date picker), preserve the time part from startTimeOnly
  useEffect(() => {
    if (!isInitializing.current && !skipDateUpdate.current && startTime) {
      const currentTimeOnly = getValues(startTimeOnlyField)
      if (currentTimeOnly) {
        // Date changed, combine with existing time (preserve time, only update date)
        const dateStr = extractDate(startTime)
        if (dateStr) {
          const combined = combineDateAndTime(dateStr, currentTimeOnly)
          if (combined) {
            skipTimeUpdate.current = true
            setValue(`orderEvents.${eventIndex}.startTime`, combined, { shouldValidate: true })
            setTimeout(() => { skipTimeUpdate.current = false }, 0)
          }
        }
      }
    }
  }, [startTime, setValue, eventIndex, getValues, startTimeOnlyField])

  // When startTimeOnly changes, combine with date from startTime (preserve date, only update time)
  useEffect(() => {
    if (!isInitializing.current && !skipTimeUpdate.current && startTimeOnly) {
      const currentStartTime = getValues(`orderEvents.${eventIndex}.startTime`)
      if (currentStartTime) {
        // Extract the date part and combine with new time (preserve date)
        const dateStr = extractDate(currentStartTime)
        if (dateStr) {
          const combined = combineDateAndTime(dateStr, startTimeOnly)
          if (combined) {
            skipDateUpdate.current = true
            setValue(`orderEvents.${eventIndex}.startTime`, combined, { shouldValidate: true })
            setTimeout(() => { skipDateUpdate.current = false }, 0)
          }
        }
      }
    }
  }, [startTimeOnly, setValue, eventIndex, getValues])

  // When endTimeOnly changes, combine with date from startTime (endTime uses startTime date)
  useEffect(() => {
    if (!isInitializing.current && !skipTimeUpdate.current && endTimeOnly) {
      const currentStartTime = getValues(`orderEvents.${eventIndex}.startTime`)
      
      if (currentStartTime) {
        // Use the date from startTime for endTime
        const dateStr = extractDate(currentStartTime)
        if (dateStr) {
          const combined = combineDateAndTime(dateStr, endTimeOnly)
          if (combined) {
            skipDateUpdate.current = true
            setValue(`orderEvents.${eventIndex}.endTime`, combined, { shouldValidate: true })
            setTimeout(() => { skipDateUpdate.current = false }, 0)
          }
        }
      }
    }
  }, [endTimeOnly, setValue, eventIndex, getValues, startTime])

  // Copy startTime to endTime if endTime is empty (only when startTime changes)
  useEffect(() => {
    if (!isInitializing.current && !skipTimeUpdate.current && startTime) {
      const currentEndTime = getValues(`orderEvents.${eventIndex}.endTime`)
      const currentEndTimeOnly = getValues(endTimeOnlyField)
      
      // If endTime is empty or endTimeOnly is empty, copy from startTime
      if (!currentEndTime || !currentEndTimeOnly) {
        const startTimeOnly = getValues(startTimeOnlyField)
        if (startTimeOnly) {
          const dateStr = extractDate(startTime)
          if (dateStr) {
            const combined = combineDateAndTime(dateStr, startTimeOnly)
            if (combined) {
              setValue(`orderEvents.${eventIndex}.endTime`, combined, { shouldValidate: true })
              setValue(endTimeOnlyField, startTimeOnly, { shouldValidate: false })
            }
          }
        } else if (startTime) {
          // No time yet, just copy the datetime
          setValue(`orderEvents.${eventIndex}.endTime`, startTime, { shouldValidate: true })
        }
      }
    }
  }, [startTime, setValue, eventIndex, getValues, startTimeOnlyField, endTimeOnlyField])

  return (
    <LoadConfirmFormContainer
      container
      spacing={2}
      data-testid="Load.Pickup.Form"
    >
      <FormTwoColumnLayout>
        <DatePickerField
          label="Pickup Date"
          name={`orderEvents.${eventIndex}.startTime`}
          highlight
          hideIcon={hideIcons}
        />

        <br />

        <TimeInputField
          label="Pickup Time From"
          name={startTimeOnlyField}
          highlight
          hideSelectIcon={hideIcons}
        />

        <TimeInputField
          label="Pickup Time To"
          name={endTimeOnlyField}
          highlight
          hideSelectIcon={hideIcons}
        />
      </FormTwoColumnLayout>

      <FormSingleColumnLayout>
        <OrderEventCompanyNameField
          eventIndex={eventIndex}
          label="Pickup Company Name"
          suggestions={companySuggestions}
          suggestionsLoading={companySuggestionsLoading}
          highlight
        />
        <TextInputField
          label="Pickup Company Address"
          name={`orderEvents.${eventIndex}.address`}
          highlight
        />
      </FormSingleColumnLayout>

      <FormTwoColumnLayout>
        <TextInputField label="FTL or LTL" name={`orderEvents.${eventIndex}.loadType`} />
        <TextInputField label="Load Count" name={`orderEvents.${eventIndex}.loadCount`} />
        <TextInputField label="Temp" name={`orderEvents.${eventIndex}.temperatureValue`} />
        <TextInputField label="Hazmat" name={`orderEvents.${eventIndex}.hazmat`} />
        <TextInputField label="Pickup#" name={`orderEvents.${eventIndex}.pickupNumber`} />
        <TextInputField label="Weight" name={`orderEvents.${eventIndex}.weightValue`} />
      </FormTwoColumnLayout>

      <FormSingleColumnLayout>
        <TextInputField
          label="Shipment Notes"
          name={`orderEvents.${eventIndex}.notes`}
          multiline
          minRows={3}
        />
      </FormSingleColumnLayout>
    </LoadConfirmFormContainer>
  )
}

export default LoadPickupForm
